/**
 * ISO 20022 External Purpose Codes for SEPA Credit Transfers
 * Based on ISO 20022 External Code Sets
 */
export interface PurposeCode {
  code: string
  name: string
  description: string
  category: string
}

export const PURPOSE_CATEGORIES = {
  CASH: 'Cash Management',
  CORT: 'Trade Services',
  HEDG: 'Treasury',
  INTC: 'Intra Company',
  LOAN: 'Loans',
  PENS: 'Pension',
  SALA: 'Salary & Benefits',
  SECU: 'Securities',
  SSBE: 'Social Security',
  SUPP: 'Supplier Payments',
  TAXS: 'Tax',
  TRAD: 'Trade',
  TREA: 'Treasury',
  OTHR: 'Other'
} as const

/**
 * Common ISO 20022 purpose codes used in SEPA transfers
 */
export const PURPOSE_CODES: PurposeCode[] = [
  // Cash Management
  { code: 'CASH', name: 'Cash Management', description: 'Cash management transfer', category: 'CASH' },
  { code: 'COLL', name: 'Collection Payment', description: 'Collection of funds', category: 'CASH' },
  { code: 'CSDB', name: 'Cash Disbursement', description: 'Cash disbursement', category: 'CASH' },
  { code: 'DEPT', name: 'Deposit', description: 'Deposit transaction', category: 'CASH' },
  { code: 'INTC', name: 'Intra Company', description: 'Intra-company payment', category: 'INTC' },

  // Trade & Suppliers
  { code: 'GDDS', name: 'Goods Purchase', description: 'Purchase of goods', category: 'TRAD' },
  { code: 'SCVE', name: 'Services Purchase', description: 'Purchase of services', category: 'TRAD' },
  { code: 'SUPP', name: 'Supplier Payment', description: 'Payment to supplier', category: 'SUPP' },
  { code: 'GDSV', name: 'Goods & Services', description: 'Purchase of goods and services', category: 'TRAD' },
  { code: 'TRADE', name: 'Trade Settlement', description: 'Trade settlement payment', category: 'TRAD' },

  // Salary & Benefits
  { code: 'SALA', name: 'Salary', description: 'Salary payment', category: 'SALA' },
  { code: 'BONU', name: 'Bonus', description: 'Bonus payment', category: 'SALA' },
  { code: 'COMM', name: 'Commission', description: 'Commission payment', category: 'SALA' },
  { code: 'PENS', name: 'Pension', description: 'Pension payment', category: 'PENS' },
  { code: 'ALLW', name: 'Allowance', description: 'Allowance payment', category: 'SALA' },
  { code: 'BEXP', name: 'Business Expenses', description: 'Business expense reimbursement', category: 'SALA' },
  { code: 'REBT', name: 'Rebate', description: 'Rebate payment', category: 'SALA' },
  { code: 'ROYA', name: 'Royalties', description: 'Royalty payment', category: 'SALA' },

  // Taxes & Government
  { code: 'TAXS', name: 'Tax Payment', description: 'Tax payment', category: 'TAXS' },
  { code: 'VATX', name: 'VAT Payment', description: 'Value added tax payment', category: 'TAXS' },
  { code: 'WHLD', name: 'Withholding', description: 'Withholding tax', category: 'TAXS' },
  { code: 'GOVT', name: 'Government', description: 'Government payment', category: 'TAXS' },
  { code: 'ESTX', name: 'Estate Tax', description: 'Estate tax payment', category: 'TAXS' },

  // Social Security & Benefits
  { code: 'SSBE', name: 'Social Security', description: 'Social security benefit', category: 'SSBE' },
  { code: 'BECH', name: 'Child Benefit', description: 'Child benefit payment', category: 'SSBE' },
  { code: 'BENE', name: 'Unemployment Benefit', description: 'Unemployment benefit', category: 'SSBE' },

  // Loans & Credit
  { code: 'LOAN', name: 'Loan', description: 'Loan payment', category: 'LOAN' },
  { code: 'LOAR', name: 'Loan Repayment', description: 'Loan repayment', category: 'LOAN' },
  { code: 'INTR', name: 'Interest', description: 'Interest payment', category: 'LOAN' },
  { code: 'PPTI', name: 'Property Insurance', description: 'Property insurance premium', category: 'LOAN' },
  { code: 'RINP', name: 'Recurring Payment', description: 'Recurring installment', category: 'LOAN' },

  // Utilities & Subscriptions
  { code: 'ELEC', name: 'Electricity', description: 'Electricity bill', category: 'OTHR' },
  { code: 'GASB', name: 'Gas', description: 'Gas bill', category: 'OTHR' },
  { code: 'WTER', name: 'Water', description: 'Water bill', category: 'OTHR' },
  { code: 'PHON', name: 'Telephone', description: 'Telephone bill', category: 'OTHR' },
  { code: 'CBFF', name: 'Cable/TV', description: 'Cable/TV bill', category: 'OTHR' },
  { code: 'NWCH', name: 'Network Charges', description: 'Network charges', category: 'OTHR' },
  { code: 'OTLC', name: 'Other Telecom', description: 'Other telecom services', category: 'OTHR' },

  // Insurance
  { code: 'INSU', name: 'Insurance', description: 'Insurance premium', category: 'OTHR' },
  { code: 'LIFI', name: 'Life Insurance', description: 'Life insurance premium', category: 'OTHR' },
  { code: 'HLTI', name: 'Health Insurance', description: 'Health insurance premium', category: 'OTHR' },

  // Real Estate & Rent
  { code: 'RENT', name: 'Rent', description: 'Rent payment', category: 'OTHR' },
  { code: 'BLDM', name: 'Building Maintenance', description: 'Building maintenance', category: 'OTHR' },
  { code: 'STDY', name: 'Study', description: 'Study/education payment', category: 'OTHR' },

  // Travel & Transport
  { code: 'TRPT', name: 'Transport', description: 'Transport charges', category: 'OTHR' },
  { code: 'AIRB', name: 'Air Travel', description: 'Air travel payment', category: 'OTHR' },
  { code: 'BUSB', name: 'Bus Travel', description: 'Bus travel payment', category: 'OTHR' },
  { code: 'RLWY', name: 'Railway', description: 'Railway payment', category: 'OTHR' },
  { code: 'FERB', name: 'Ferry', description: 'Ferry payment', category: 'OTHR' },
  { code: 'TAXC', name: 'Taxi', description: 'Taxi charges', category: 'OTHR' },

  // Healthcare
  { code: 'MDCS', name: 'Medical Services', description: 'Medical services', category: 'OTHR' },
  { code: 'DNTS', name: 'Dental Services', description: 'Dental services', category: 'OTHR' },
  { code: 'HLTC', name: 'Home Health Care', description: 'Home health care', category: 'OTHR' },

  // Financial Services
  { code: 'ACCT', name: 'Account Management', description: 'Account management', category: 'OTHR' },
  { code: 'CBTV', name: 'Cable TV Bill', description: 'Cable TV bill', category: 'OTHR' },
  { code: 'CCRD', name: 'Credit Card', description: 'Credit card payment', category: 'OTHR' },
  { code: 'CDCB', name: 'Card Payment', description: 'Card payment with cash back', category: 'OTHR' },
  { code: 'CDCD', name: 'Cash Deposit', description: 'Cash card deposit', category: 'OTHR' },
  { code: 'CDCS', name: 'Cash Settlement', description: 'Card cash settlement', category: 'OTHR' },

  // Investments & Securities
  { code: 'DIVI', name: 'Dividend', description: 'Dividend payment', category: 'SECU' },
  { code: 'SECU', name: 'Securities', description: 'Securities transaction', category: 'SECU' },
  { code: 'TREA', name: 'Treasury', description: 'Treasury payment', category: 'TREA' },
  { code: 'FWBC', name: 'Forward FX', description: 'Forward foreign exchange', category: 'TREA' },
  { code: 'HEDG', name: 'Hedging', description: 'Hedging transaction', category: 'HEDG' },

  // Membership & Subscriptions
  { code: 'MSVC', name: 'Membership', description: 'Membership services', category: 'OTHR' },
  { code: 'SUBS', name: 'Subscription', description: 'Subscription payment', category: 'OTHR' },

  // Legal & Professional
  { code: 'CSLP', name: 'Legal Services', description: 'Company social loan payment', category: 'OTHR' },
  { code: 'LICF', name: 'License Fee', description: 'License fee', category: 'OTHR' },

  // Donations & Charity
  { code: 'CHAR', name: 'Charity', description: 'Charity payment', category: 'OTHR' },

  // Other
  { code: 'OTHR', name: 'Other', description: 'Other payment', category: 'OTHR' },
  { code: 'ADVA', name: 'Advance', description: 'Advance payment', category: 'OTHR' },
  { code: 'AGRT', name: 'Agreement', description: 'Agricultural transfer', category: 'OTHR' },
  { code: 'CLPR', name: 'Car Loan', description: 'Car loan principal', category: 'LOAN' },
  { code: 'COMC', name: 'Commercial', description: 'Commercial payment', category: 'TRAD' },
  { code: 'COST', name: 'Costs', description: 'Cost payment', category: 'OTHR' },
  { code: 'CPYR', name: 'Copyright', description: 'Copyright payment', category: 'OTHR' },
  { code: 'GVEA', name: 'Government Employee', description: 'Austrian government employee', category: 'SALA' },
  { code: 'GVEB', name: 'Government Employee', description: 'Government employee payment', category: 'SALA' },
  { code: 'GWLT', name: 'Gambling', description: 'Gambling winnings/lottery', category: 'OTHR' },
  { code: 'HREC', name: 'Home Remodeling', description: 'Home remodeling', category: 'OTHR' },
  { code: 'ICCP', name: 'Insurance Claim', description: 'Irrevocable credit card payment', category: 'OTHR' },
  { code: 'IDCP', name: 'Irrevocable Debit', description: 'Irrevocable debit card payment', category: 'OTHR' },
  { code: 'IHRP', name: 'Installment', description: 'Installment hire purchase', category: 'LOAN' },
  { code: 'IVPT', name: 'Invoice', description: 'Invoice payment', category: 'TRAD' },
  { code: 'MCDM', name: 'Multi-Currency', description: 'Multi-currency account management', category: 'CASH' },
  { code: 'MTUP', name: 'Mobile Top-Up', description: 'Mobile phone top-up', category: 'OTHR' },
  { code: 'NETT', name: 'Netting', description: 'Netting payment', category: 'CASH' },
  { code: 'PADD', name: 'Pre-authorized Debit', description: 'Pre-authorized debit', category: 'CASH' },
  { code: 'PTSP', name: 'Payment Terms', description: 'Payment terms', category: 'TRAD' },
  { code: 'RCKE', name: 'Re-presented Check', description: 'Re-presented check entry', category: 'CASH' },
  { code: 'REFU', name: 'Refund', description: 'Refund payment', category: 'OTHR' },
  { code: 'RELG', name: 'Religious', description: 'Rent/lease of grounds', category: 'OTHR' },
  { code: 'RIMB', name: 'Reimbursement', description: 'Reimbursement of previous transaction', category: 'OTHR' },
  { code: 'RPNT', name: 'Recurring Payment', description: 'Rent payment', category: 'OTHR' },
  { code: 'RVPM', name: 'Reversal', description: 'Reversal payment', category: 'CASH' },
  { code: 'SLEB', name: 'Self-employed', description: 'Self-employed benefit', category: 'SSBE' },
  { code: 'SPLT', name: 'Split Payment', description: 'Split payment', category: 'CASH' },
  { code: 'TBIL', name: 'Telecommunications', description: 'Telecommunications bill', category: 'OTHR' },
  { code: 'TCSC', name: 'Town Council', description: 'Town council service charges', category: 'OTHR' },
  { code: 'TELI', name: 'Telephone', description: 'Telephone-initiated', category: 'OTHR' },
  { code: 'TPRI', name: 'Third Party', description: 'TRI payment', category: 'OTHR' },
  { code: 'UNCO', name: 'Unstructured', description: 'Unstructured payment', category: 'OTHR' },
  { code: 'VIEW', name: 'Vision Care', description: 'Vision care', category: 'OTHR' },
  { code: 'WEBI', name: 'Web-Initiated', description: 'Web-initiated', category: 'OTHR' },
]

/**
 * Get purpose code by code string
 */
export function getPurposeCode(code: string): PurposeCode | undefined {
  return PURPOSE_CODES.find(p => p.code === code.toUpperCase())
}

/**
 * Default purpose code for general payments
 */
export const DEFAULT_PURPOSE_CODE = 'OTHR'

/**
 * Commonly used purpose codes (shown first in dropdown)
 */
export const COMMON_PURPOSE_CODES = [
  'OTHR', 'GDDS', 'SCVE', 'SUPP', 'SALA', 'RENT', 'IVPT', 'LOAN', 'TAXS', 'INSU'
]

/**
 * Get purpose codes sorted with common ones first
 */
export function getSortedPurposeCodes(): PurposeCode[] {
  const common = COMMON_PURPOSE_CODES
    .map(code => PURPOSE_CODES.find(p => p.code === code))
    .filter((p): p is PurposeCode => p !== undefined)

  const others = PURPOSE_CODES
    .filter(p => !COMMON_PURPOSE_CODES.includes(p.code))
    .sort((a, b) => a.name.localeCompare(b.name))

  return [...common, ...others]
}

